import { useState, useEffect, useCallback, useMemo } from 'react'
import type { Account, Category, CreateCategoryInput, Member } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useTheme, type ThemeMode } from '../contexts/ThemeContext'
import { useAmountUnit } from '../contexts/AmountUnitContext'
import type { AmountUnit } from '../lib/amount-format'
import type { Lang } from '../i18n/translations'
import { accountsApi, categoriesApi, csvApi, authApi, membersApi } from '../lib/api'
import { calculateAge, todayIso } from '../lib/age'
import { useModalReset } from '../lib/use-modal-reset'
import {
  FORMAT_LABEL_KEYS,
  FORMAT_NOTE_KEYS,
  INSTITUTION_FORMATS,
  importRowsInChunks,
  matchesFormat,
  parseInstitutionCsvFile,
  type CsvFormat,
  type ImportRow,
  type InstitutionFormat,
} from '../lib/csv-import'
import WiseIntegrationTab from './settings/WiseIntegration'
import { btnDangerSm, btnPrimary, btnSecondary, btnSecondarySm, modalTitle, pageTitle, subheading } from '../lib/ui'

type Tab = 'members' | 'categories' | 'csv' | 'integrations' | 'security' | 'language' | 'appearance'

interface CategoryFormData {
  name: string
  type: 'income' | 'expense'
  icon: string
  color: string
}

const defaultFormData = (): CategoryFormData => ({
  name: '',
  type: 'expense',
  icon: '📦',
  color: '#6366F1',
})

interface CategoryModalProps {
  open: boolean
  editing: Category | null
  onClose: () => void
  onSaved: () => void
  token: string
}

function CategoryModal({ open, editing, onClose, onSaved, token }: CategoryModalProps) {
  const { t } = useLanguage()
  const [form, setForm] = useState<CategoryFormData>(defaultFormData())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useModalReset(open, editing?.id ?? null, () => {
    if (editing) {
      setForm({ name: editing.name, type: editing.type as 'income' | 'expense', icon: editing.icon, color: editing.color })
    } else {
      setForm(defaultFormData())
    }
    setError(null)
  })

  if (!open) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const input: CreateCategoryInput = { name: form.name, type: form.type, icon: form.icon, color: form.color }
      if (editing) {
        await categoriesApi.update(token, editing.id, { name: form.name, icon: form.icon, color: form.color })
      } else {
        await categoriesApi.create(token, input)
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        <h2 className={`${modalTitle} mb-4`}>{editing ? t('category_modal_edit') : t('category_modal_add')}</h2>
        {error && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{error}</p>}
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('category_icon_label')}</label>
            <input
              type="text"
              value={form.icon}
              onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              maxLength={2}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('category_name_label')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              maxLength={20}
              required
            />
          </div>
          {!editing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('category_type_label')}</label>
              <div className="flex gap-3">
                {(['expense', 'income'] as const).map((type) => (
                  <label key={type} className="flex items-center gap-1.5 text-sm cursor-pointer dark:text-gray-300">
                    <input type="radio" name="type" value={type} checked={form.type === type} onChange={() => setForm((f) => ({ ...f, type }))} />
                    {type === 'expense' ? t('type_expense') : t('type_income')}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('category_color_label')}</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                className="w-10 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">{form.color}</span>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className={btnSecondary}>
              {t('common_cancel')}
            </button>
            <button type="submit" disabled={submitting} className={btnPrimary}>
              {submitting ? t('common_saving') : t('common_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CategoriesTab() {
  const { accessToken: token } = useAuth()
  const { t } = useLanguage()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Category | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!token) return
    try {
      const res = await categoriesApi.list(token)
      setCategories(res.data)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { void load() }, [load])

  const handleDelete = async (cat: Category) => {
    if (!token) return
    if (!confirm(t('categories_delete_confirm').replace('{name}', cat.name))) return
    setError(null)
    try {
      await categoriesApi.delete(token, cat.id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_delete_error'))
    }
  }

  const customCategories = categories.filter((c) => !c.isDefault)
  const defaultCategories = categories.filter((c) => c.isDefault)

  const renderList = (items: Category[], actions: { canEdit: boolean; canDelete: boolean }) => (
    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
      {items.map((cat) => (
        <li key={cat.id} className="flex items-center justify-between py-2.5 px-1">
          <div className="flex items-center gap-3">
            <span className="text-xl">{cat.icon}</span>
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{cat.name}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {cat.type === 'income' ? t('type_income') : cat.type === 'expense' ? t('type_expense') : t('type_transfer')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {cat.isDefault && (
              <span className="text-xs text-gray-400 dark:text-gray-500">{t('categories_default_badge')}</span>
            )}
            {actions.canEdit && (
              <button
                onClick={() => { setEditing(cat); setModalOpen(true) }}
                className={btnSecondarySm}
              >
                {t('categories_edit')}
              </button>
            )}
            {actions.canDelete && (
              <button
                onClick={() => { void handleDelete(cat) }}
                className={btnDangerSm}
              >
                {t('categories_delete')}
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  )

  if (loading) return <p className="text-gray-500 text-sm dark:text-gray-400">{t('common_loading')}</p>

  return (
    <div>
      {error && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{error}</p>}
      <div className="flex justify-between items-center mb-4">
        <h3 className={subheading}>{t('categories_custom')}（{customCategories.length}/30）</h3>
        <button
          onClick={() => { setEditing(null); setModalOpen(true) }}
          className={btnPrimary}
        >
          {t('categories_add')}
        </button>
      </div>
      {customCategories.length === 0 ? (
        <p className="text-gray-400 text-sm mb-4 dark:text-gray-500">{t('categories_empty')}</p>
      ) : (
        <div className="mb-4 border border-gray-200 rounded-lg px-3 dark:border-gray-700">{renderList(customCategories, { canEdit: true, canDelete: true })}</div>
      )}

      <h3 className={`${subheading} mb-2`}>{t('categories_default')}</h3>
      <div className="border border-gray-200 rounded-lg px-3 dark:border-gray-700">{renderList(defaultCategories, { canEdit: false, canDelete: true })}</div>

      <CategoryModal
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => { void load() }}
        token={token ?? ''}
      />
    </div>
  )
}

// ─────────────────────────────────────────────
// メンバー（資産の持ち主）
//
// ログインユーザーとは別の「誰の口座か」を表すラベル。口座に紐づけると
// ダッシュボードで人ごとの資産内訳が出る。
// ─────────────────────────────────────────────

const DEFAULT_MEMBER_COLOR = '#6366f1'

interface MemberModalProps {
  open: boolean
  editing: Member | null
  onClose: () => void
  onSaved: () => void
  token: string
}

function MemberModal({ open, editing, onClose, onSaved, token }: MemberModalProps) {
  const { t } = useLanguage()
  const [name, setName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [color, setColor] = useState(DEFAULT_MEMBER_COLOR)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useModalReset(open, editing?.id ?? null, () => {
    setName(editing?.name ?? '')
    setBirthDate(editing?.birthDate ?? '')
    setColor(editing?.color ?? DEFAULT_MEMBER_COLOR)
    setError(null)
  })

  if (!open) return null

  const age = calculateAge(birthDate)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      // 空欄は「未入力」。null を送って登録済みの生年月日を消せるようにする
      const nextBirthDate = birthDate || null
      if (editing) {
        await membersApi.update(token, editing.id, { name, birthDate: nextBirthDate, color })
      } else {
        await membersApi.create(token, { name, birthDate: nextBirthDate, color })
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        <h2 className={`${modalTitle} mb-4`}>{editing ? t('member_modal_edit') : t('member_modal_add')}</h2>
        {error && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{error}</p>}
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('member_name_label')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('member_name_placeholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              maxLength={30}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('member_birth_date_label')}
            </label>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              max={todayIso()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
              {age === null
                ? t('member_birth_date_hint')
                : t('member_age_display').replace('{age}', String(age))}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('member_color_label')}</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">{color}</span>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className={btnSecondary}>
              {t('common_cancel')}
            </button>
            <button type="submit" disabled={submitting} className={btnPrimary}>
              {submitting ? t('common_saving') : t('common_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function MembersTab() {
  const { accessToken: token } = useAuth()
  const { t } = useLanguage()
  const [members, setMembers] = useState<Member[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Member | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 口座も併せて取る。紐づく口座が残っているメンバーは削除できないため、
  // 件数を先に見せて「まず口座の所有者を変える」と気付けるようにする
  const load = useCallback(async () => {
    if (!token) return
    try {
      const [membersRes, accountsRes] = await Promise.all([
        membersApi.list(token),
        accountsApi.list(token, { includeArchived: true }),
      ])
      setMembers(membersRes.data)
      setAccounts(accountsRes.data)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { void load() }, [load])

  const handleDelete = async (member: Member) => {
    if (!token) return
    if (!confirm(t('members_delete_confirm').replace('{name}', member.name))) return
    setError(null)
    try {
      await membersApi.delete(token, member.id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_delete_error'))
    }
  }

  const accountCountByMember = useMemo(() => {
    const counts = new Map<string, number>()
    for (const account of accounts) {
      if (!account.memberId) continue
      counts.set(account.memberId, (counts.get(account.memberId) ?? 0) + 1)
    }
    return counts
  }, [accounts])

  if (loading) return <p className="text-gray-500 text-sm dark:text-gray-400">{t('common_loading')}</p>

  return (
    <div>
      {error && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{error}</p>}
      <div className="flex justify-between items-start gap-4 mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('members_desc')}</p>
        <button
          onClick={() => { setEditing(null); setModalOpen(true) }}
          className={`${btnPrimary} shrink-0`}
        >
          {t('members_add')}
        </button>
      </div>

      {members.length === 0 ? (
        <p className="text-gray-400 text-sm dark:text-gray-500">{t('members_empty')}</p>
      ) : (
        <div className="border border-gray-200 rounded-lg px-3 dark:border-gray-700">
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {members.map((member) => {
              const age = calculateAge(member.birthDate)
              return (
                <li key={member.id} className="flex items-center justify-between py-2.5 px-1">
                  <div className="flex items-center gap-3">
                    <span
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: member.color ?? DEFAULT_MEMBER_COLOR }}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{member.name}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {t('members_accounts_count').replace('{count}', String(accountCountByMember.get(member.id) ?? 0))}
                        {age !== null && <> ・ {t('member_age_display').replace('{age}', String(age))}</>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setEditing(member); setModalOpen(true) }}
                      className={btnSecondarySm}
                    >
                      {t('members_edit')}
                    </button>
                    <button
                      onClick={() => { void handleDelete(member) }}
                      className={btnDangerSm}
                    >
                      {t('members_delete')}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <MemberModal
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => { void load() }}
        token={token ?? ''}
      />
    </div>
  )
}

const ACCOUNT_REQUIRED_FORMATS: readonly CsvFormat[] = INSTITUTION_FORMATS

function CsvTab() {
  const { accessToken: token } = useAuth()
  const { t } = useLanguage()
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<string | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [format, setFormat] = useState<CsvFormat>('generic')
  const [accounts, setAccounts] = useState<Account[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [importAccountId, setImportAccountId] = useState('')
  const accountRequired = ACCOUNT_REQUIRED_FORMATS.includes(format)

  // 選択中のフォーマットに対応する口座だけを候補にする。金融機関名は自由入力で
  // 未設定にもできるため、1件も一致しないときは絞り込みをやめて全口座から選ばせる。
  const matchedAccounts = useMemo(
    () => (format === 'generic' ? [] : accounts.filter((acc) => matchesFormat(acc, format))),
    [accounts, format],
  )
  const accountOptions = matchedAccounts.length > 0 ? matchedAccounts : accounts
  const noAccountMatch = accountRequired && accounts.length > 0 && matchedAccounts.length === 0

  useEffect(() => {
    if (!token) return
    void accountsApi.list(token).then((res) => setAccounts(res.data)).catch(() => undefined)
    void categoriesApi.list(token).then((res) => setCategories(res.data)).catch(() => undefined)
  }, [token])

  // フォーマットを切り替えたら取り込み先の選択をリセットする。候補が1件だけなら
  // それを選んでおく（フォーマットと口座は基本1対1で対応するため）。
  // 絞り込みが効かず全口座を出す場合も必ずリセットする。前のフォーマットで選ばれた
  // 口座が残っていると、別のカードの明細を誤った口座へ取り込んでしまう。
  useEffect(() => {
    setImportAccountId(matchedAccounts.length === 1 ? matchedAccounts[0].id : '')
  }, [matchedAccounts])

  const handleExport = async () => {
    if (!token) return
    setExporting(true)
    try {
      await csvApi.exportTransactions(token)
    } finally {
      setExporting(false)
    }
  }

  const parseGeneric = async (file: File): Promise<ImportRow[]> => {
    const text = await file.text()
    const lines = text.trim().split('\n')
    const header = lines[0].toLowerCase()
    if (!header.includes('date') || !header.includes('amount')) {
      throw new Error(t('transactions_csv_invalid_header'))
    }
    const cols = header.split(',').map((c) => c.trim().replace(/"/g, ''))
    return lines.slice(1).map((line) => {
      const vals = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''))
      const obj: Record<string, string> = {}
      cols.forEach((col, i) => { obj[col] = vals[i] ?? '' })
      return {
        date: obj.date,
        type: obj.type as 'income' | 'expense',
        category: obj.category,
        description: obj.description,
        amount: parseFloat(obj.amount),
        accountId: obj.accountid ?? obj.account_id ?? '',
        currency: obj.currency || 'JPY',
        note: obj.note || undefined,
        // 書き出しでは 1 / 空欄。Zeni の CSV を取り込み直しても集計対象外を保つ
        excluded: obj.excluded === '1' || obj.excluded === 'true',
      }
    }).filter((r) => r.date && r.amount > 0)
  }

  const parseInstitution = async (file: File, csvFormat: InstitutionFormat): Promise<ImportRow[]> => {
    if (!importAccountId) throw new Error(t('csv_import_select_account_error'))
    return parseInstitutionCsvFile(file, csvFormat, importAccountId, categories)
  }

  const parseFile = async (file: File): Promise<ImportRow[]> =>
    format === 'generic' ? parseGeneric(file) : parseInstitution(file, format)

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0 || !token) return
    setImporting(true)
    setImportResult(null)
    setImportError(null)

    try {
      let imported = 0
      let skipped = 0
      for (const file of files) {
        const rows = await parseFile(file)
        if (rows.length === 0) {
          throw new Error(t('csv_import_file_no_rows').replace('{name}', file.name))
        }
        const res = await importRowsInChunks(token, rows)
        imported += res.imported
        skipped += res.skipped
      }
      setImportResult(
        t('transactions_csv_imported_result')
          .replace('{count}', String(imported))
          .replace('{skipped}', String(skipped)),
      )
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className={`${subheading} mb-2`}>{t('csv_export_title')}</h3>
        <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{t('csv_export_desc')}</p>
        <button
          onClick={() => { void handleExport() }}
          disabled={exporting}
          className={btnPrimary}
        >
          {exporting ? t('csv_exporting') : t('csv_export_btn')}
        </button>
      </div>

      <div>
        <h3 className={`${subheading} mb-2`}>{t('csv_import_title')}</h3>
        <p className="text-sm text-gray-500 mb-3 dark:text-gray-400">{t('csv_import_desc')}</p>

        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('csv_import_format_label')}</label>
        <select
          value={format}
          onChange={(e) => { setFormat(e.target.value as CsvFormat); setImportResult(null); setImportError(null) }}
          className="mb-3 w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="generic">{t('csv_format_generic')}</option>
          {INSTITUTION_FORMATS.map((f) => (
            <option key={f} value={f}>{t(FORMAT_LABEL_KEYS[f])}</option>
          ))}
        </select>

        {!accountRequired ? (
          <p className="text-xs text-gray-400 mb-4 font-mono bg-gray-50 rounded p-2 dark:text-gray-500 dark:bg-gray-800">
            header: date,type,category,description,amount,accountId,currency,note
          </p>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('csv_import_account_label')}</label>
            <select
              value={importAccountId}
              onChange={(e) => setImportAccountId(e.target.value)}
              className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">{t('csv_import_account_placeholder')}</option>
              {accountOptions.map((acc) => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>
            {noAccountMatch && (
              <p className="text-xs text-amber-600 mt-2 dark:text-amber-500">
                {t('csv_import_account_no_match')}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2 dark:text-gray-500">
              {format !== 'generic' && t(FORMAT_NOTE_KEYS[format])}
            </p>
          </div>
        )}

        {importResult && <p className="text-green-600 text-sm mb-3 dark:text-green-400">{importResult}</p>}
        {importError && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{importError}</p>}
        <label className={`inline-block px-4 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 ${importing || (accountRequired && !importAccountId) ? 'opacity-50 pointer-events-none' : ''}`}>
          {importing ? t('csv_importing') : t('csv_import_btn')}
          <input type="file" accept=".csv" multiple className="hidden" onChange={(e) => { void handleImport(e) }} />
        </label>
      </div>
    </div>
  )
}

function SecurityTab() {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setMessage(null)
    setError(null)
    try {
      const res = await authApi.passwordResetRequest(email)
      setMessage(res.data.message)
      setEmail('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_send_error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <h3 className={`${subheading} mb-2`}>{t('security_reset_title')}</h3>
      <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{t('security_reset_desc')}</p>
      {message && <p className="text-green-600 text-sm mb-3 dark:text-green-400">{message}</p>}
      {error && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{error}</p>}
      <form onSubmit={(e) => { void handlePasswordReset(e) }} className="flex gap-3 max-w-md">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('security_email_placeholder')}
          required
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        <button
          type="submit"
          disabled={submitting}
          className={btnPrimary}
        >
          {submitting ? t('common_sending') : t('common_send')}
        </button>
      </form>
    </div>
  )
}

function LanguageTab() {
  const { lang, setLang, t } = useLanguage()

  const options: { value: Lang; label: string }[] = [
    { value: 'ja', label: t('language_ja') },
    { value: 'en', label: t('language_en') },
  ]

  return (
    <div>
      <h3 className={`${subheading} mb-4`}>{t('language_title')}</h3>
      <div className="flex flex-col gap-3 max-w-xs">
        {options.map(({ value, label }) => (
          <label key={value} className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="lang"
              value={value}
              checked={lang === value}
              onChange={() => setLang(value)}
              className="accent-indigo-600"
            />
            <span className="text-sm text-gray-800 dark:text-gray-200">{label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function AppearanceTab() {
  const { themeMode, setThemeMode } = useTheme()
  const { unit, setUnit } = useAmountUnit()
  const { t } = useLanguage()

  const themeOptions: { value: ThemeMode; label: string }[] = [
    { value: 'system', label: t('appearance_system') },
    { value: 'light', label: t('appearance_light') },
    { value: 'dark', label: t('appearance_dark') },
  ]

  const unitOptions: { value: AmountUnit; label: string }[] = [
    { value: 'yen', label: t('amount_unit_yen') },
    { value: 'man', label: t('amount_unit_man') },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h3 className={`${subheading} mb-4`}>{t('appearance_title')}</h3>
        <div className="flex flex-col gap-3 max-w-xs">
          {themeOptions.map(({ value, label }) => (
            <label key={value} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="theme"
                value={value}
                checked={themeMode === value}
                onChange={() => setThemeMode(value)}
                className="accent-indigo-600"
              />
              <span className="text-sm text-gray-800 dark:text-gray-200">{label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <h3 className={`${subheading} mb-1`}>{t('amount_unit_title')}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{t('amount_unit_desc')}</p>
        <div className="flex flex-col gap-3 max-w-xs">
          {unitOptions.map(({ value, label }) => (
            <label key={value} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="amount-unit"
                value={value}
                checked={unit === value}
                onChange={() => setUnit(value)}
                className="accent-indigo-600"
                data-testid={`amount-unit-${value}`}
              />
              <span className="text-sm text-gray-800 dark:text-gray-200">{label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function Settings() {
  const { t } = useLanguage()
  const initialTab = (new URLSearchParams(window.location.search).get('tab') as Tab | null) ?? 'categories'
  const [activeTab, setActiveTab] = useState<Tab>(initialTab)

  const tabs: { key: Tab; label: string }[] = [
    { key: 'members', label: t('tab_members') },
    { key: 'categories', label: t('tab_categories') },
    { key: 'csv', label: t('tab_csv') },
    { key: 'integrations', label: t('tab_integrations') },
    { key: 'security', label: t('tab_security') },
    { key: 'language', label: t('tab_language') },
    { key: 'appearance', label: t('tab_appearance') },
  ]

  return (
    /* md 以上では見出しとタブ列を固定し、本文カードだけスクロールさせる */
    <div className="md:h-full md:flex md:flex-col md:min-h-0">
      <h1 className={`${pageTitle} mb-4 md:shrink-0`}>{t('settings_title')}</h1>
      <div className="flex flex-col md:flex-row gap-4 md:items-start md:flex-1 md:min-h-0">
        <nav className="flex md:flex-col gap-1 w-full md:w-44 md:shrink-0 overflow-x-auto md:max-h-full md:overflow-y-auto">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium text-left transition-colors whitespace-nowrap shrink-0 md:shrink ${
                activeTab === key
                  ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex-1 min-w-0 md:h-full md:overflow-y-auto">
          {activeTab === 'members' && <MembersTab />}
          {activeTab === 'categories' && <CategoriesTab />}
          {activeTab === 'csv' && <CsvTab />}
          {activeTab === 'integrations' && <WiseIntegrationTab />}
          {activeTab === 'security' && <SecurityTab />}
          {activeTab === 'language' && <LanguageTab />}
          {activeTab === 'appearance' && <AppearanceTab />}
        </div>
      </div>
    </div>
  )
}
